/**
 * Tolerant validator for year/period qualifier fields (100$d, 110$d, 400$d, 410$d).
 *
 * Catalogers enter dates in the full breadth of MARC/AACR2/RDA conventions, not
 * just a plain 4-digit year: approximate dates (`ca. 1920`, `asi 1920`), supplied
 * dates (`[1920]`), uncertain years (`1867?`), open/closed ranges, Czech decade
 * and century notation (`50. léta`, `19. století`), and BCE dates, as well as
 * full ISO/EDTF dates. The backend persists whatever is entered verbatim (the
 * `value` columns are TEXT, no server-side format check), so this validator MUST
 * NOT block legitimate cataloging input.
 *
 * Severity policy (CZNA-235):
 * - Recognized date shapes return `{ valid: true }` (no UI noise).
 * - An inverted numeric range returns a non-blocking `warning`.
 * - Anything unrecognized that still contains a plausible year returns a
 *   non-blocking `warning` ("unusual format — double-check") rather than an error,
 *   because it still saves correctly.
 * - A hard `error` is reserved for input with no date signal at all (no digits),
 *   i.e. a value clearly typed into the wrong field.
 *
 * Previously this rejected everything outside a narrow whitelist with a hard
 * error, which surfaced a red "invalid format" message on perfectly valid MARC
 * dates and made catalogers believe the record would not save.
 *
 * Designed as a pure, side-effect-free function so it can be plugged into
 * react-hook-form's `validate` prop or called directly. Empty/whitespace input
 * is treated as `valid: true` — required-ness is enforced separately.
 */
var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
import { peelIsbdFraming } from './dateStatement';
/** I18n keys for validator messages. Consumers translate them via `intl.formatMessage`. */
export var YEAR_QUALIFIER_INVALID_KEY = 'proposalForm.validation.yearQualifierInvalid';
export var YEAR_QUALIFIER_INVERTED_RANGE_KEY = 'proposalForm.validation.yearQualifierInvertedRange';
export var YEAR_QUALIFIER_UNRECOGNIZED_KEY = 'proposalForm.validation.yearQualifierUnrecognized';
/**
 * The grammar is built from ONE date point rather than from a list of whole-value
 * shapes (CZNA-783). The old form enumerated the shapes it accepted — `SINGLE`,
 * `CENTURY`, `RANGE_TO_DECADE`, … — so every combination of two features needed
 * its own entry, and the ones nobody enumerated fell through to the "unusual
 * format" warning. Measured against the live corpus that was 106 944 of
 * 1 265 926 `$d` occurrences (8.45 %) warned, all of them ordinary NK practice:
 * a cataloger who sees a yellow complaint on every second historical person
 * reasonably reports it as "the form only takes four digits", which is the
 * ticket this comment is under. Composing from a point makes
 * `činná 4. století př. Kr.` accepted because each of its parts is, not because
 * someone thought to write that combination down.
 *
 * <p><b>The vocabulary is measured, not guessed.</b> Across all 1 265 926 `$d`
 * values the corpus contains exactly 21 distinct alphabetic tokens: `asi`,
 * `činný`, `činná`, `století`, `př`, `kr`, `po`, the twelve Czech month names,
 * and one `(Fleur R.)` that is genuinely text in the wrong field. `ca`, `cca`,
 * `circa`, `kolem`, `přibližně`, `stol.` and `léta` are kept although the corpus
 * has none: they are NK-plausible and cost nothing to accept.
 */
/** `ca.` / `cca` / `asi` / `kolem` / `přibližně` — approximation, before the number. */
var APPROX = String.raw(templateObject_1 || (templateObject_1 = __makeTemplateObject(["(?:circa|cca|ca|asi|kolem|p\u0159ibli\u017En\u011B|p\u0159ibl).?s*"], ["(?:circa|cca|ca|asi|kolem|p\u0159ibli\u017En\u011B|p\u0159ibl)\\.?\\s*"])));
/**
 * `činný` / `činná` — floruit, i.e. "active in", said of a person whose birth
 * and death are unknown. 28 605 occurrences, the second-largest vocabulary item
 * after `asi`, and previously unrecognised in every one of them.
 */
var ACTIVITY = String.raw(templateObject_2 || (templateObject_2 = __makeTemplateObject(["(?:\u010Dinn[\u00FD\u00E1\u00E9\u00ED]).?s*"], ["(?:\u010Dinn[\u00FD\u00E1\u00E9\u00ED])\\.?\\s*"])));
/**
 * Era suffix. `po Kr.` and `po n. l.` (AD, spelled out) are here because the
 * corpus uses them 359 times while only their `př.` counterparts were accepted —
 * so `43 př. Kr.-asi 18 po Kr.` warned on its second half alone.
 */
var ERA = String.raw(templateObject_3 || (templateObject_3 = __makeTemplateObject(["(?:s*(?:p\u0159.?s*n.?s*l.?|p\u0159.?s*kr.?|po.?s*n.?s*l.?|po.?s*kr.?|n.?s*l.?|bce|bc|ad))?"], ["(?:\\s*(?:p\u0159\\.?\\s*n\\.?\\s*l\\.?|p\u0159\\.?\\s*kr\\.?|po\\.?\\s*n\\.?\\s*l\\.?|po\\.?\\s*kr\\.?|n\\.?\\s*l\\.?|bce|bc|ad))?"])));
/**
 * The twelve Czech month names, for the long date form `1920 duben 15.`.
 *
 * <p>Each is written stem-first with an optional tail and an optional abbreviating
 * period, because the corporate corpus abbreviates: of its 13 alphabetic tokens
 * (CZNA-876 measured the field's vocabulary the same way CZNA-783 measured the
 * personal one) eleven are months and two of those are `list.` and `ún.`.
 * `červen(?:ec)?` covers červen and červenec in one alternative, and what makes
 * that safe is that the optional tail is GREEDY — `červenec` matches in full
 * because `(?:ec)?` prefers to consume, not because of where the alternative sits
 * in the group. Reordering the group changes no outcome.
 *
 * <p>Side effect worth knowing: a bare stem now validates too (`1920 led`,
 * `1920 zář`). Neither corpus contains one — both vocabularies are closed and
 * measured — so this is over-tolerance in a validator whose worst case is a
 * missing warning, not a wrong accept of stored data.
 */
var MONTH = String.raw(templateObject_4 || (templateObject_4 = __makeTemplateObject(["(?:led(?:en)?|\u00FAn(?:or)?|b\u0159ez(?:en)?|dub(?:en)?|kv\u011Bt(?:en)?|\u010Derven(?:ec)?|srp(?:en)?|z\u00E1\u0159(?:\u00ED)?|\u0159\u00EDj(?:en)?|list(?:opad)?|pros(?:inec)?).?"], ["(?:led(?:en)?|\u00FAn(?:or)?|b\u0159ez(?:en)?|dub(?:en)?|kv\u011Bt(?:en)?|\u010Derven(?:ec)?|srp(?:en)?|z\u00E1\u0159(?:\u00ED)?|\u0159\u00EDj(?:en)?|list(?:opad)?|pros(?:inec)?)\\.?"])));
/** `N. století` / `N. stol.` — century. Usable as a point, so ranges of centuries compose. */
var CENTURY_NUM = String.raw(templateObject_5 || (templateObject_5 = __makeTemplateObject(["d{1,2}.s*(?:stol.?|stolet\u00ED)"], ["\\d{1,2}\\.\\s*(?:stol\\.?|stolet\u00ED)"])));
/** `NN. léta` — Czech decade notation without a century. */
var DECADE_NUM = String.raw(templateObject_6 || (templateObject_6 = __makeTemplateObject(["d{1,2}.s*l\u00E9ta"], ["\\d{1,2}\\.\\s*l\u00E9ta"])));
/**
 * `YYYY měsíc D.` — the Czech long date, as catalogued (`1920 duben 15.`).
 *
 * <p>The day is optional (CZNA-876): a meeting qualifier is routinely catalogued
 * to the month alone (`(1961 leden : $c Praha)`), and requiring the day left six
 * such corporate values warning about a date that is perfectly well formed.
 */
var LONG_DATE_NUM = String.raw(templateObject_7 || (templateObject_7 = __makeTemplateObject(["d{1,4}s+", "(?:s+d{1,2}.?)?"], ["\\d{1,4}\\s+", "(?:\\s+\\d{1,2}\\.?)?"])), MONTH);
/** The numeric core of a point, longest alternative first so `1920 duben 15.` wins over `1920`. */
var POINT_NUM = String.raw(templateObject_8 || (templateObject_8 = __makeTemplateObject(["(?:", "|", "|", "|d{1,4})"], ["(?:", "|", "|", "|\\d{1,4})"])), LONG_DATE_NUM, CENTURY_NUM, DECADE_NUM);
/**
 * One date point: optional approximation and/or floruit marker (either order —
 * `asi` and `činný` are independent qualifiers and the corpus is not consistent
 * about which comes first), an optional `[` from a supplied date, the numeric
 * core, an optional `?` uncertainty marker, an optional `]`, an optional era.
 */
var POINT = String.raw(templateObject_9 || (templateObject_9 = __makeTemplateObject(["(?:", ")?(?:", ")?(?:", ")?[?s*", "s*??s*]?", ""], ["(?:", ")?(?:", ")?(?:", ")?\\[?\\s*", "\\s*\\??\\s*\\]?", ""])), APPROX, ACTIVITY, APPROX, POINT_NUM, ERA);
/**
 * The whole value. A leading floruit marker is hoisted out because it qualifies
 * the WHOLE statement, not one endpoint — `činný 16. století-17. století` says a
 * person was active across two centuries, and `činný` appears once.
 */
var LEAD = String.raw(templateObject_10 || (templateObject_10 = __makeTemplateObject(["(?:", ")?"], ["(?:", ")?"])), ACTIVITY);
/** YEAR — a single point. */
var SINGLE = new RegExp(String.raw(templateObject_11 || (templateObject_11 = __makeTemplateObject(["^", "", "$"], ["^", "", "$"])), LEAD, POINT), 'i');
/** YEAR-YEAR — closed range (`1966-1992`, `činný 16. století-17. století`). */
var CLOSED_RANGE = new RegExp(String.raw(templateObject_12 || (templateObject_12 = __makeTemplateObject(["^", "", "s*-s*", "$"], ["^", "", "\\s*-\\s*", "$"])), LEAD, POINT, POINT), 'i');
/** YEAR- — open-ended, still living / still active (`1960-`). */
var OPEN_END_RANGE = new RegExp(String.raw(templateObject_13 || (templateObject_13 = __makeTemplateObject(["^", "", "s*-s*$"], ["^", "", "\\s*-\\s*$"])), LEAD, POINT), 'i');
/** -YEAR — birth unknown (`-1918`). */
var OPEN_START_RANGE = new RegExp(String.raw(templateObject_14 || (templateObject_14 = __makeTemplateObject(["^-s*", "", "$"], ["^-\\s*", "", "$"])), LEAD, POINT), 'i');
/**
 * `13.-17. století` — a century range with the noun stated once, which is how
 * Czech elides it. Needs its own matcher because the first endpoint is a bare
 * ordinal that carries no century word of its own, so it cannot be a POINT.
 */
var ELIDED_CENTURY_RANGE = new RegExp(String.raw(templateObject_15 || (templateObject_15 = __makeTemplateObject(["^", "d{1,2}.s*-s*", "$"], ["^", "\\d{1,2}\\.\\s*-\\s*", "$"])), LEAD, POINT), 'i');
/** YEAR nebo/or YEAR — disjunction (`1920 nebo 1921`). */
var OR_PAIR = new RegExp(String.raw(templateObject_16 || (templateObject_16 = __makeTemplateObject(["^", "", "s+(?:nebo|or)s+", "$"], ["^", "", "\\s+(?:nebo|or)\\s+", "$"])), LEAD, POINT, POINT), 'i');
/** ISO / EDTF full dates that a date picker or paste may produce. */
var ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
var EDTF_BASIC = /^\d{8}$/;
/** Czech day.month.year in digits (`3. 5. 1920`). */
var DMY = /^\d{1,2}\.\s*\d{1,2}\.\s*\d{1,4}$/;
/** Matchers that accept a value outright with no warning. */
var CLEAN_MATCHERS = [
    SINGLE,
    ELIDED_CENTURY_RANGE,
    OPEN_END_RANGE,
    OPEN_START_RANGE,
    OR_PAIR,
    ISO_DATE,
    EDTF_BASIC,
    DMY,
];
/**
 * A plain numeric range, i.e. both endpoints are bare years — the only shape the
 * inverted-range check may look at. `1920 duben 15.-1985 leden 3.` yields the
 * digit run `[1920, 15, 1985, 3]`, and comparing first against last would call
 * that transposed; a century range compares 16 against 17 by luck rather than by
 * meaning. Restricting the check keeps it honest, and it still covers every one
 * of the 67 genuinely inverted occurrences the corpus holds — 65 `YYYY-YYYY`
 * plus `asi 1740-1532` and `1943-asi 1908`, which is why an approximation
 * prefix is allowed here but a century, decade or long-date core is not.
 *
 * <p>The narrowing is what makes widening the grammar safe. Long dates used to
 * warn as "unrecognised" and so never reached this check; now that they are
 * accepted, a first-run-against-last-run comparison would read all 862 of the
 * corpus's `1944 únor 23.-` as transposed, because 23 is less than 1944.
 *
 * <p>A leading floruit marker is allowed through so `činný 1740-1532` is still
 * caught. An `n. l.` / `AD` suffix is NOT, so `1920-1900 n. l.` no longer warns
 * where it once did — a deliberate trade, not an oversight: the corpus holds
 * zero occurrences, and admitting era suffixes here means re-deriving which
 * ones reverse the ordering, which is the reasoning the shape check exists to
 * avoid.
 */
var PLAIN_NUMERIC_RANGE = new RegExp(String.raw(templateObject_17 || (templateObject_17 = __makeTemplateObject(["^s*", "(?:", ")?[?s*(d{1,4})s*??s*]?s*-s*(?:", ")?[?s*(d{1,4})s*??s*]?s*$"], ["^\\s*", "(?:", ")?\\[?\\s*(\\d{1,4})\\s*\\??\\s*\\]?\\s*-\\s*(?:", ")?\\[?\\s*(\\d{1,4})\\s*\\??\\s*\\]?\\s*$"])), LEAD, APPROX, APPROX), 'i');
/** Detects whether a string contains at least one run of digits (a plausible year). */
var hasDigits = function (s) { return /\d/.test(s); };
/**
 * Validate a year/period qualifier string.
 *
 * Whitespace-tolerant. Normalises Unicode en-dash (–) and em-dash (—) to a plain
 * hyphen and collapses internal whitespace before matching, so users can paste
 * from Word/PDF without the input being rejected.
 */
export var validateYearQualifier = function (text) {
    // Required-ness is enforced elsewhere; an empty value here is "no opinion".
    if (text == null)
        return { valid: true };
    var trimmed = text.trim();
    if (trimmed.length === 0)
        return { valid: true };
    // Normalise en-dash / em-dash to ASCII hyphen, collapse internal whitespace.
    var normalised = trimmed.replace(/[–—]/g, '-').replace(/\s+/g, ' ');
    // AACR2 closes the date statement with a period, and the corpus does so 74 977
    // times — `1685-1750.` alone is 68 % of everything this validator used to warn
    // about. Strip ONE trailing period for matching only. The guard is that what
    // remains must still carry a digit, so a lone "." cannot be normalised into an
    // empty string and out through the accept-empty branch above.
    var matchable = normalised.replace(/\.$/, '').trim();
    var withoutPeriod = hasDigits(matchable) ? matchable : normalised;
    // Corporate `$d` carries the ISBD punctuation of the qualifier group it sits in
    // — `2003 :`, `(2006 :`, `1464)` — because MARC keeps that punctuation in the
    // subfield it belongs to. The date is what sits inside the framing, so judge
    // that; the framing itself is never a reason to complain. Measured over the
    // 5 987 corporate values, this alone is 4 961 warnings (82,9 %) that were about
    // a colon. Same guard: peeling never yields a digit-free candidate.
    // See `dateStatement.ts` for why the framing is cataloguing rather than noise.
    var candidate = peelIsbdFraming(withoutPeriod);
    // Closed numeric range: accept, but warn when the second year precedes the
    // first (likely a transposition). Skip the inversion check when an era marker
    // is present, since BCE ordering is reversed.
    if (CLOSED_RANGE.test(candidate)) {
        var plain = candidate.match(PLAIN_NUMERIC_RANGE);
        if (plain) {
            var start = parseInt(plain[1], 10);
            var end = parseInt(plain[2], 10);
            if (end < start) {
                return { valid: true, level: 'warning', message: YEAR_QUALIFIER_INVERTED_RANGE_KEY };
            }
        }
        return { valid: true };
    }
    // Recognised single-point / decade / century / full-date shapes — accept clean.
    if (CLEAN_MATCHERS.some(function (re) { return re.test(candidate); })) {
        return { valid: true };
    }
    // Unrecognised, but it still contains a plausible year: accept with a gentle,
    // non-blocking warning. The value saves correctly; the hint just flags an
    // unusual format the cataloger may want to double-check.
    if (hasDigits(normalised)) {
        return { valid: true, level: 'warning', message: YEAR_QUALIFIER_UNRECOGNIZED_KEY };
    }
    // No digits at all — almost certainly text typed into the wrong field.
    return { valid: false, level: 'error', message: YEAR_QUALIFIER_INVALID_KEY };
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8, templateObject_9, templateObject_10, templateObject_11, templateObject_12, templateObject_13, templateObject_14, templateObject_15, templateObject_16, templateObject_17;
